// @greenlight-spec: 19-design-system.md
import { type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { type AppStatus } from '@greenlight/shared';

// App-status presentation: maps the live app statuses (and pipeline
// statuses) to StatusBadge variants + labels.
// Shared by the apps list and the app-detail surfaces.

export type BadgeMeta = { variant: StatusBadgeVariant; label: string };

export const STATUS_META: Record<AppStatus, BadgeMeta> = {
  registering: { variant: 'pending', label: 'Registering' },
  registered: { variant: 'pending', label: 'Registered' },
  building: { variant: 'deploying', label: 'Building' },
  deploying: { variant: 'deploying', label: 'Deploying' },
  active: { variant: 'active', label: 'Active' },
  degraded: { variant: 'degraded', label: 'Degraded' },
  failed: { variant: 'failed', label: 'Failed' },
  suspended: { variant: 'inactive', label: 'Paused' },
  queued_for_deletion: { variant: 'inactive', label: 'Queued for deletion' },
  deleted: { variant: 'inactive', label: 'Deleted' },
};

// `status` arrives as a string from the API; fall back gracefully for any value
// outside the known set rather than crashing on an undefined lookup.
export function statusMeta(status: string): BadgeMeta {
  return (
    (STATUS_META as Record<string, BadgeMeta>)[status] ?? { variant: 'inactive', label: status }
  );
}

export const PIPELINE_META: Record<string, BadgeMeta> = {
  passed: { variant: 'active', label: 'Passed' },
  failed: { variant: 'failed', label: 'Failed' },
  running: { variant: 'deploying', label: 'Running' },
  // Non-terminal: scanners passed, the deploy stage is in flight (15-database-schema.md).
  deploying: { variant: 'deploying', label: 'Deploying' },
  cancelled: { variant: 'inactive', label: 'Cancelled' },
};

// `status` arrives as a string from the API; fall back gracefully for any value
// outside the known set rather than crashing on an undefined lookup.
export function pipelineMeta(status: string): BadgeMeta {
  return PIPELINE_META[status] ?? { variant: 'inactive', label: status };
}
